import { FC, useEffect, useMemo, useState } from 'react'

import { styled } from 'styled-components'

import { StyledButton } from 'peach/components/Button/Button'
import FullPage from 'peach/components/Legacy/Page/FullPage'
import { useStorageState } from 'peach/hooks'

import { R } from 'core/helpers'
import { ResponseAPIKeyNoSecretAPIVersioning, Role } from 'core/types'

import Complete from './Complete'
import CreateAppDomainsForm from './CreateAppDomainsForm'
import CreateCompanyForm from './CreateCompanyForm'
import CreateInvestorForm from './CreateInvestorForm'
import CreateRoleForm from './CreateRoleForm'
import CreateTeamForm from './CreateTeamForm'
import CreateUserAndAPIKeyForm from './CreateUserAndAPIKeyForm'
import CreateUsersForm from './CreateUsersForm'
import { Company, ExtendedRole, Storage } from './types'

const STEPS = [
  'Create company',
  'Create role: API',
  'Create role: Admin',
  'Create role: Agent',
  'Create role: Supervisor',
  'Create role: Borrower',
  'Create role: peach-support',
  'Create investor: Lender',
  'Create user & API key: Peach super admin user',
  'Create user & API key: Lender service user',
  'Create team: "Peach Tier 2 Support"',
  'Create users & employees: Peach',
  'Update app domains: Lender',
  'Complete',
]

const CreateCompany: FC = () => {
  const [step, setStep] = useState<number | null>(0)
  const [reset, setReset] = useState(false)
  const [apiKeys, setApiKeys] = useState<
    Array<ResponseAPIKeyNoSecretAPIVersioning>
  >([])

  const [createCompany, setCreateCompany] = useStorageState<Storage>(
    'createCompany',
    null,
  )

  const company: Company = useMemo(
    () => R.find(createCompany ?? [], (c) => c?.completed === false) ?? {},
    [createCompany],
  )

  const updateCompany = (data: Company) =>
    setCreateCompany([
      data,
      ...R.reject(createCompany ?? [], (c) => c?.companyId === data?.companyId),
    ])

  // For newly created companies, don't fetch roles from API - they don't exist yet
  // Roles are stored in company.roles state and added there as they're created via CreateRoleForm
  const roles = (company?.roles ?? []) as Array<Role>

  const common = {
    title: STEPS[step ?? 0],
    company,
    updateCompany,
  }

  const userAndAPIKeyCommon = {
    ...common,
    apiKeys,
    setApiKeys,
  }

  useEffect(() => {
    if (R.isNil(company) || R.isEmpty(company)) {
      setStep(0)
    } else if (
      R.find(
        company.appDomains ?? [],
        (domain) => domain.type === 'borrower',
      ) &&
      R.find(company.appDomains ?? [], (domain) => domain.type === 'agent') &&
      R.find(company.appDomains ?? [], (domain) => domain.type === 'admin')
    ) {
      setStep(13)
    } else if (
      // All we care about is that there are more than 2 users at this step.
      // It's possible that the default set of Peach users will be different
      // so we're not going to specifically check the auth emails of any of the
      // users here.
      R.length(company.users ?? []) > 2
    ) {
      setStep(12)
    } else if (
      R.find(
        company?.teams ?? [],
        (team) =>
          team.name === 'Peach Tier 2 Support' &&
          team.companyId === company?.companyId,
      )
    ) {
      setStep(11)
    } else if (
      R.find(company?.users ?? [], (user) => user.userType === 'lender')
    ) {
      setStep(10)
    } else if (
      R.find(company?.users ?? [], (user) => user.userType === 'peach')
    ) {
      setStep(9)
    } else if (R.length(company.investors ?? []) > 0) {
      setStep(8)
    } else if (
      R.find(
        company.roles ?? [],
        (role: ExtendedRole) => role.alias === 'peach-support',
      )
    ) {
      setStep(7)
    } else if (
      R.find(
        company.roles ?? [],
        (role: ExtendedRole) => role.alias === 'borrower',
      )
    ) {
      setStep(6)
    } else if (
      R.find(
        company.roles ?? [],
        (role: ExtendedRole) => role.alias === 'supervisor',
      )
    ) {
      setStep(5)
    } else if (
      R.isArray(company?.roles) &&
      R.find(company.roles, (role: ExtendedRole) => role.alias === 'agent')
    ) {
      setStep(4)
    } else if (
      R.isArray(company?.roles) &&
      R.find(company.roles, (role: ExtendedRole) => role.alias === 'admin')
    ) {
      setStep(3)
    } else if (
      R.isArray(company?.roles) &&
      R.find(company.roles, (role: ExtendedRole) => role.alias === 'api')
    ) {
      setStep(2)
    } else if (company?.companyId) {
      setStep(1)
    }
  }, [company, roles])

  return (
    <FullPage>
      <TopContainer>
        <Title>Create Company</Title>
        <ActionsContainer>
          {reset ? (
            <ActionsContainer>
              <AreYouSure>
                Are you sure you want to reset everything? This will start the
                "Create Company" flow to the beginning.
              </AreYouSure>
              <ButtonsContainer>
                <StyledButton
                  $primary
                  onClick={() => {
                    setCreateCompany(
                      R.reject(
                        createCompany ?? [],
                        (c) => c?.companyId === company?.companyId,
                      ),
                    )
                    setReset(false)
                  }}
                  type='button'
                >
                  Confirm
                </StyledButton>
                <StyledButton onClick={() => setReset(false)} type='button'>
                  Cancel
                </StyledButton>
              </ButtonsContainer>
            </ActionsContainer>
          ) : (
            <StyledButton
              disabled={R.isNil(createCompany) || R.isEmpty(createCompany)}
              onClick={() => setReset(true)}
              type='button'
            >
              Reset
            </StyledButton>
          )}
        </ActionsContainer>
      </TopContainer>
      <Container>
        <Cell>
          <Steps>
            {STEPS.map((s, i) => (
              <Step $current={step === i} key={i}>
                {s}
              </Step>
            ))}
          </Steps>
          {!R.isEmpty(company) && (
            <NewCompanyJson>
              <pre>{JSON.stringify(company, null, 2)}</pre>
            </NewCompanyJson>
          )}
        </Cell>
        <Cell>
          {step === 0 ? (
            <CreateCompanyForm title={STEPS[0]} updateCompany={updateCompany} />
          ) : step === 1 ? (
            <CreateRoleForm alias='api' {...common} />
          ) : step === 2 ? (
            <CreateRoleForm alias='admin' {...common} />
          ) : step === 3 ? (
            <CreateRoleForm alias='agent' {...common} />
          ) : step === 4 ? (
            <CreateRoleForm alias='supervisor' {...common} />
          ) : step === 5 ? (
            <CreateRoleForm alias='borrower' {...common} />
          ) : step === 6 ? (
            <CreateRoleForm alias='peach-support' {...common} />
          ) : step === 7 ? (
            <CreateInvestorForm {...common} />
          ) : step === 8 ? (
            <CreateUserAndAPIKeyForm
              key='peach'
              userType='peach'
              {...userAndAPIKeyCommon}
            />
          ) : step === 9 ? (
            <CreateUserAndAPIKeyForm
              key='lender'
              userType='lender'
              {...userAndAPIKeyCommon}
            />
          ) : step === 10 ? (
            <CreateTeamForm {...common} />
          ) : step === 11 ? (
            <CreateUsersForm {...common} />
          ) : step === 12 ? (
            <CreateAppDomainsForm {...common} />
          ) : step === 13 ? (
            <Complete {...common} />
          ) : null}
        </Cell>
      </Container>
    </FullPage>
  )
}

export default CreateCompany

const TopContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const Title = styled.div`
  margin-bottom: 16px;
  color: ${(p) => p.theme.text};
  font-size: 16px;
  font-weight: 600;
`

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: end;
  margin-bottom: 16px;
`

const AreYouSure = styled.div`
  margin-bottom: 8px;
  width: 380px;
  font-size: 12px;
`

const ButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
`

const Container = styled.div`
  display: grid;
  grid-template-columns: 320px auto;
`

const Cell = styled.div`
  border: 1px solid ${(p) => p.theme.border};
  padding: 24px 16px;
`

const Steps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const Step = styled.div<{ $current: boolean }>`
  ${(p) => (p.$current ? 'opacity: 1' : 'opacity: 0.7')};
  color: ${(p) => p.theme.text};
  font-size: 14px;
  font-weight: 600;
`

const NewCompanyJson = styled.div<{ $showAll?: boolean }>`
  margin-top: 16px;
  border-radius: 4px;
  background-color: ${(p) => p.theme.hover};
  padding: 4px 8px;
  white-space: break-spaces;
  color: ${(p) => p.theme.text};
  font-size: 10px;

  pre {
    height: auto;
    overflow: auto;
  }
`
